import React, { useCallback, useEffect, useRef, useState } from "react";
import { Files, FileText, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Clock } from "lucide-react";
import {
  uploadDocument, getNote, isNoteDone, isNoteFailed, claimStatusOf,
  reprocessNote, findRecentVisit,
} from "@/lib/noteClaim";
import { PatientMatchCell } from "@/components/claim/PatientMatch";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const describe = (err) => err?.response?.data?.detail || err?.message || "Something went wrong";
const POLL_MS = 4500;              // every ~4–5s
const REVIEW_CUTOFF_MS = 10 * 60 * 1000; // still going after 10 min ⇒ review later, never re-sent

const STATUS = {
  queued: { label: "Queued", cls: "bg-black/[.05] text-[#6e6e73]" },
  uploading: { label: "Uploading", cls: "bg-[#ececfb] text-[#5856D6]" },
  processing: { label: "Coding…", cls: "bg-[#ececfb] text-[#5856D6]" },
  ready: { label: "Ready", cls: "bg-[#e7f6ee] text-[#1f9d57]" },
  failed: { label: "Failed", cls: "bg-[#fbe9e7] text-[#c22219]" },
  review_later: { label: "Still processing", cls: "bg-[#fbf1de] text-[#b06f00]" },
};
const cptCount = (n) => (Array.isArray(n?.cpt_codes) ? n.cpt_codes.length : 0);

// Batch → many claims. Each document is its OWN visit, processed strictly one at a time
// for the provider: upload → poll to ready/failed → next. A visit still going after 10 min
// is flagged "review later" and left alone. A created-but-failed visit is re-run in place
// (reprocess), never uploaded again.
export default function MultiClaimFlow({ files, npi, clientId, onFinish, onBack, onProgress }) {
  const [items, setItems] = useState(() => (files || []).map((f) => ({ name: f.name, size: f.size, status: "queued", note: null, error: null })));
  const cancelled = useRef(false);
  const startedFor = useRef(null);

  const update = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  // Poll one visit until it is ready/failed or the 10-minute cutoff; returns the note or
  // null on the cutoff (caller marks "review later" and never re-sends).
  const pollVisit = useCallback(async (id) => {
    const t0 = Date.now();
    while (Date.now() - t0 < REVIEW_CUTOFF_MS) {
      if (cancelled.current) return null;
      const n = await getNote(id).catch(() => null);
      if (n && isNoteDone(n)) return n;
      await sleep(POLL_MS);
    }
    return null; // cutoff — review later
  }, []);

  // Process one document: upload once (adopting a just-created visit instead of a blind
  // re-send), poll, and reprocess-in-place if coding failed.
  const runOne = useCallback(async (file, i) => {
    try {
      update(i, { status: "uploading", error: null });
      let created;
      try {
        created = await uploadDocument(file, npi);
      } catch (err) {
        // Never blind re-send: a just-created visit for this file wins over re-uploading.
        const recent = await findRecentVisit({ npi, filename: file.name, size: file.size, clientId });
        if (!recent) throw err;
        created = recent;
      }
      if (cancelled.current) return;
      if (!created?.id) throw new Error("Upload didn't return a visit");
      update(i, { status: "processing", note: created });
      const done = await pollVisit(created.id);
      if (cancelled.current) return;
      if (!done) { update(i, { status: "review_later", note: created }); return; }
      update(i, { status: claimStatusOf(done), note: done });
    } catch (err) {
      if (!cancelled.current) update(i, { status: "failed", error: describe(err) });
    }
  }, [npi, clientId, pollVisit]);

  // Strictly sequential — one visit at a time for this provider.
  const runAll = useCallback(async (list) => {
    for (let i = 0; i < list.length; i++) {
      if (cancelled.current) return;
      await runOne(list[i], i);
    }
  }, [runOne]);

  useEffect(() => {
    cancelled.current = false;
    if (files?.length && startedFor.current !== files) {
      startedFor.current = files;
      runAll(files);
    }
    return () => { cancelled.current = true; };
  }, [files, runAll]);

  // Retry a failed item: re-run in place if the visit already exists (reprocess), else re-upload.
  const retry = async (i) => {
    const it = items[i];
    if (it?.note?.id) {
      update(i, { status: "processing", error: null });
      try {
        await reprocessNote(it.note.id);
        const done = await pollVisit(it.note.id);
        update(i, done ? { status: claimStatusOf(done), note: done } : { status: "review_later" });
      } catch (err) { update(i, { status: "failed", error: describe(err) }); }
      return;
    }
    runOne(files[i], i);
  };

  const settled = items.filter((it) => ["ready", "failed", "review_later"].includes(it.status)).length;
  const codedOk = items.filter((it) => it.status === "ready").length;
  const allDone = settled === items.length;

  useEffect(() => { onProgress?.(settled, items.length); }, [settled, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="px-6 pt-4 pb-4" data-testid="cc-multi-panel">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-6 h-6 rounded-[7px] bg-[#1d1d1f] text-white flex items-center justify-center flex-shrink-0"><Files size={13} /></div>
        <div className="text-[13px] font-bold text-[#1d1d1f]">Documents → claims</div>
        <span className="text-[10px] font-semibold text-[#6e6e73] ml-1">{items.length} files · one visit at a time · each becomes its own claim</span>
      </div>

      <div className="rounded-[13px] border border-black/[.07] bg-black/[.02] divide-y divide-black/[.05]">
        {items.map((it, i) => {
          const st = STATUS[it.status] || STATUS.queued;
          const settledRow = ["ready", "failed", "review_later"].includes(it.status);
          return (
            <div key={i} className="px-4 py-3" data-testid={`cc-multi-item-${i}`}>
              <div className="flex items-center gap-3">
                <FileText size={14} className="text-[#aeaeb2] flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-[#1d1d1f] truncate">{it.name}</div>
                  {it.status === "ready" && it.note ? (
                    <div className="text-[10.5px] text-[#6e6e73] mt-0.5 truncate">{cptCount(it.note)} CPT{cptCount(it.note) === 1 ? "" : "s"} coded</div>
                  ) : it.status === "failed" ? (
                    <div className="text-[10.5px] text-[#c22219] mt-0.5 truncate">{it.error || "Coding failed"}</div>
                  ) : it.status === "review_later" ? (
                    <div className="text-[10.5px] text-[#b06f00] mt-0.5 truncate">Taking longer than usual — review it later; it is not re-sent.</div>
                  ) : null}
                </div>
                {it.status === "failed" ? (
                  <button onClick={() => retry(i)} className="text-[10.5px] font-semibold text-[#5856D6] inline-flex items-center gap-1 flex-shrink-0" data-testid={`cc-multi-retry-${i}`}><RefreshCw size={11} /> {it.note?.id ? "Re-run coding" : "Retry"}</button>
                ) : null}
                <span className={`text-[9.5px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 flex-shrink-0 ${st.cls}`}>
                  {it.status === "uploading" || it.status === "processing" ? <Loader2 size={10} className="animate-spin" /> : it.status === "ready" ? <CheckCircle2 size={10} /> : it.status === "failed" ? <AlertTriangle size={10} /> : it.status === "review_later" ? <Clock size={10} /> : null}
                  {st.label}
                </span>
              </div>
              {/* patient per the match rules + fix-a-no-match, once matching has run */}
              {settledRow && it.note && (it.note.match_status || it.note.patient_name) ? (
                <div className="mt-2 pl-[26px]">
                  <PatientMatchCell note={it.note} clientId={clientId} onResolved={(n) => update(i, { note: n, status: claimStatusOf(n) })} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-[11px] text-[#6e6e73]" data-testid="cc-multi-progress">
          {allDone ? `${codedOk} of ${items.length} claim${items.length === 1 ? "" : "s"} ready · you can leave any time` : `Coding one at a time… ${settled} of ${items.length} done`}
        </div>
        <div className="flex items-center gap-2">
          {onBack ? <button onClick={onBack} className="text-[12px] font-semibold text-[#6e6e73] hover:text-[#1d1d1f] transition" data-testid="cc-multi-back">Back</button> : null}
          <button onClick={onFinish}
            className="text-[12px] font-bold px-4 py-2 rounded-[9px] bg-[#1d1d1f] text-white transition" data-testid="cc-multi-done">
            {allDone ? "Done" : "Leave — runs in the background"}
          </button>
        </div>
      </div>
    </div>
  );
}
